import dayjs, { Dayjs } from 'dayjs';
import { Duration } from 'dayjs/plugin/duration';
import { HumanizeDuration, HumanizeDurationLanguage, UnitName } from 'humanize-duration-ts';

export interface DurationResult {
  asMillisecondsValue: number;
  asNumberValue: number;
  asString: string;
}

export default class CustomDuration {
  private static langService: HumanizeDurationLanguage = new HumanizeDurationLanguage();
  private static humanizer: HumanizeDuration = new HumanizeDuration(CustomDuration.langService);

  /**
   * Computes the duration between two dates, humanized in months, or in days when shorter than one month.
   * @param startDate Start of the period
   * @param endDate End of the period
   * @param unitOfTime Unit used for the raw diff value
   * @return The duration in milliseconds (or unitOfTime), in whole months, and as a localized string
   */
  public static calculate(startDate: Date | Dayjs, endDate: Date | Dayjs, unitOfTime: any = 'millisecond'): DurationResult {
    const diffMilliseconds = dayjs(endDate).diff(dayjs(startDate), unitOfTime);
    const diffMonths = dayjs(endDate).diff(dayjs(startDate), 'month');
    const navigatorLanguage = navigator.language.split('-')[0];
    const language = CustomDuration.humanizer.getSupportedLanguages().includes(navigatorLanguage) ? navigatorLanguage : 'fr';
    const units: UnitName[] = diffMonths < 1 ? ['d'] : ['mo'];
    return {
      asMillisecondsValue: diffMilliseconds,
      asNumberValue: diffMonths,
      asString: CustomDuration.humanizer.humanize(diffMilliseconds, { language: language, round: true, units })
    };
  }

  public static humanize(duration: Duration, units: UnitName[] = ['y', 'mo', 'd']): string {
    const navigatorLanguage = navigator.language.split('-')[0];
    const language = CustomDuration.humanizer.getSupportedLanguages().includes(navigatorLanguage) ? navigatorLanguage : 'fr';
    return CustomDuration.humanizer.humanize(duration.asMilliseconds(), { language: language, round: true, units });
  }
}

